import {
    Box,
    Button,
    Card,
    CardBody,
    CardHeader,
    Heading,
    HStack,
    Spacer,
    Spinner,
    Stack,
    StackDivider,
    Text
} from '@chakra-ui/react';
import React, {FormEvent, useEffect, useRef, useState} from 'react';
import InputRequiredRef from '../../../interfaces/InputRef';
import InputRef from '../../../interfaces/InputRef';
import InputSelect from '../../parts/InputSelect';
import ButtonSubmit from '../../parts/ButtonSubmit';
import InputDate from '../../parts/InputDate';
import Option from '../../../interfaces/Option';
import InputSelectRef from '../../../interfaces/InputSelectRef';
import RequiredMark from '../../parts/RequiredMark';
import {useAuth} from '../../../auth/AuthProvider';
import {EditIcon} from '@chakra-ui/icons';
import {Scout} from '../../../model/scout';
import {useApiProvider} from '../../../api/ApiProvider';
import ErrorMessage from '../../parts/ErrorMessage';
import InputNumberDate from "../../parts/InputNumberDate";
import InputNumberDateRef from "../../../interfaces/InputNumberDateRef";
import {GenderType, OrganizationType} from "../../../utils/organizationType";
import InputText from "../../parts/InputText";
import LoadingWidget from "../../parts/LoadingWidget";

const ScoutProfile: React.FC = () => {
    const provider = useApiProvider();

    const translateKey: { [key: string]: string } = {
        ScoutRank: 'Stopień harcerski',
        ScoutPledgeDate: 'Data złożenia przyrzeczenia',
        CorporateId: 'Numer książeczki służbowej',
        InstructorRank: 'Stopień instruktorski',
        ScoutFleurDeLisId: 'Numer lilijki instruktorskiej',
        PwdDate: 'Data zdobycia pwd',
        PhmDate: 'Data zdobycia phm',
        HmDate: 'Data zdobycia hm',
        ScoutRegionName: 'Jednostka'
      };

    const ScoutRankOptions : Option[] = [
        { label: 'ćwik/sam.', value: 'Cwik' },
        { label: 'wędr./HO', value: 'HO' },
        { label: 'HR', value: 'HR' }
    ];

    const instructorRangeOptions : Option[] = [
        { label: 'PWD', value: 'Pwd' },
        { label: 'PHM', value: 'Phm' },
        { label: 'HM', value: 'Hm' }
    ];

    const userAuth = useAuth();

    const ScoutRankRef = useRef<InputSelectRef>();
    const InstructorRankRef = useRef<InputSelectRef>();
    const pwdDateRef = useRef<InputRequiredRef>();
    const phmDateRef = useRef<InputRequiredRef>();
    const hmDateRef = useRef<InputRequiredRef>();
    const corporateIDRef = useRef<InputRequiredRef>();
    const scoutFleurDeLisIDRef = useRef<InputRequiredRef>();
    const scoutPledgeDateRef = useRef<InputNumberDateRef>();
    const unitRef = useRef<InputRef>();
    
    const [scout, setScout] = useState<Scout | null>(null);
    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const [isFetched, setIsFetched] = useState<boolean>(false);

    const [selectedScoutRankOption, setSelectedScoutRankOption] = useState<Option | undefined>((scout) ? ScoutRankOptions.at(scout?.ScoutRank) : undefined);
    const [selectedInstructorRangeOption, setSelectedInstructorRangeOption] = useState<Option | undefined>((scout) ? instructorRangeOptions.at(scout?.InstructorRank) : undefined);

    const [submittingError, setSubmittingError] = useState<string>("");
    const [submitting, setSubmitting] = useState<boolean>(false);
    
    useEffect(() => {
        provider.getScout()
            .then(scout => {
                setSelectedScoutRankOption(ScoutRankOptions[scout.ScoutRank]);
                setSelectedInstructorRangeOption(instructorRangeOptions[scout.InstructorRank]);
                setScout(scout);
            })
            .catch(err => console.error(err))
            .finally(() => setIsFetched(true));
    }, [userAuth.user]);
    console.log(scout);
    

    const [scoutRegions, setScoutRegions] = useState<Option[]>();
    const [selectedScoutRegion, setSelectedScoutRegion] = useState<Option | undefined>(undefined);
    const [areScoutRegionsFetched, setAreScoutRegionsFetched] = useState<boolean>(false);
    useEffect(() => {
        provider.getScoutRegions(userAuth.user?.Gender === GenderType.Women.toString() ? OrganizationType.Girls : OrganizationType.Boys)
            .then(data => {setScoutRegions(data.map(unit => {
                return {
                    label: unit.Name,
                    value: unit.Id,
                } as Option
            }));
                console.log("data: ", data)
            })
            .catch(err => console.error(err))
            .finally(() => setAreScoutRegionsFetched(true));
        console.log("scoutRegions: " + scoutRegions);
    }, []);


    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();

        if (unitRef.current?.validate() &&
            ScoutRankRef.current?.validate() &&
            InstructorRankRef.current?.validate() &&
            (['PWD', 'PHM', 'HM'].includes(selectedInstructorRangeOption?.label!) ? pwdDateRef.current?.validate() : true) &&
            (['PHM', 'HM'].includes(selectedInstructorRangeOption?.label!) ? phmDateRef.current?.validate() : true) &&
            (['HM'].includes(selectedInstructorRangeOption?.label!) ? hmDateRef.current?.validate() : true) &&
            (corporateIDRef.current?.value() ? corporateIDRef.current?.validate() : true) &&
            (scoutFleurDeLisIDRef.current?.value() ? scoutFleurDeLisIDRef.current?.validate() : true)
        ) {

            setSubmitting(true);
            setSubmittingError("");

            let data : any = {
                scoutRank : selectedScoutRankOption!.value,
                corporateId : corporateIDRef.current?.value() ?? '',
                instructorRank : selectedInstructorRangeOption!.value,
                scoutFleurDeLisId : scoutFleurDeLisIDRef.current?.value() ?? '',
                scoutRegionId: selectedScoutRegion!.value
            };

            if (scoutPledgeDateRef.current?.day() != null) data.scoutPledgeDay = scoutPledgeDateRef.current.day();
            if (scoutPledgeDateRef.current?.month() != null) data.ScoutPledgeMonth = scoutPledgeDateRef.current.month();
            if (scoutPledgeDateRef.current?.year() != null) data.ScoutPledgeYear = scoutPledgeDateRef.current.year();

            if (pwdDateRef.current?.value())
            {
                data.pwdDate = new Date(pwdDateRef.current?.value()).toISOString();
            }

            if (phmDateRef.current?.value())
            {
                data.phmDate = new Date(phmDateRef.current?.value()).toISOString();
            }

            if (hmDateRef.current?.value())
            {
                data.hmDate = new Date(hmDateRef.current?.value()).toISOString();
            }                        

            if (isEditMode) {
                data.Id = scout!.Id;
                console.log(data);

                provider.updateScout(data as object)
                    .then(scout => {
                        setScout(scout);
                        setIsEditMode(false);
                    })
                    .catch(err => setSubmittingError(err))
                    .finally(() => setSubmitting(false));
            }
            else {
                data.instructorId = userAuth.user!.Id!;
                console.log(data);
                provider.postScout(data as object)
                    .then(scout => setScout(scout))
                    .catch(err => setSubmittingError(err))
                    .finally(() => setSubmitting(false));
            }
        }       
    };

    const startEdit = () => { setIsEditMode(true); }
    const cancelEdit = () => { setIsEditMode(false); }


    if (isFetched)
    {
        if (scout && !isEditMode) {
            return (
                <Box
                    display="flex"
                    alignItems="start"
                    justifyContent="center"
                    height="100%"
                    width="100%"
                    pt="89px"
                >
                    <Card
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        shadow="none"
                        padding="10"
                        width="100%"
                        maxWidth="500px"
                    >
                        <CardHeader>
                            <HStack>
                                <Heading size='md'>Dane harcerskie</Heading>
                                <Spacer />
                                <Button onClick={() => startEdit()}>
                                    <EditIcon />
                                </Button>
                            </HStack>  
                        </CardHeader>
                        <CardBody
                            width="100%"
                        >
                            <Stack divider={<StackDivider />} spacing='4'>
                            {Object.entries(scout)
                            .filter(([key, ]) => !['Id', 'InstructorId', 'ScoutRegionId'].includes(key))
                            .filter(([key, value]) =>  value || (key !== "PhmDate" &&  key !== "HmDate"))
                            .map(([key, value]) => (
                                <Box key={key}>
                                <Heading size='xs' textTransform='uppercase'>
                                    {translateKey[key]}
                                </Heading>
                                <HStack>
                                {(value === "") ? (
                                    <Text as='i' pt='2' fontSize='sm'>
                                        b.d.
                                    </Text>
                                ) : (
                                    <Text pt='2' fontSize='sm'>
                                        {
                                            (key === "InstructorRank") ? instructorRangeOptions[scout.InstructorRank].label :
                                            (key === "ScoutRank") ? ScoutRankOptions[scout.ScoutRank].label :
                                            value
                                        }
                                    </Text>
                                )}
                                </HStack>      
                                </Box>
                            ))}
                            </Stack>
                        </CardBody>
                    </Card>
                </Box>
            )
        }
        else {
            return (
                <Box
                    minHeight="100vh"
                    width="100vw"
                    backgroundImage="url('/images/cone.jpg')"
                    backgroundSize="cover"
                    backgroundPosition="center"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                >
                    <Box
                        bg="white"
                        mt="89px"
                        padding="5"
                        borderRadius="md"
                        boxShadow="lg"
                        textAlign="center"
                        width="2xl"
                        display="flex"
                        flexDirection="column"
                    >
                        <Heading 
                            padding="3" 
                            as="h2" 
                            size="lg"
                        >
                            Dane harcerskie
                        </Heading>
                        <form onSubmit={handleSubmit} style={{height:"-webkit-fill-available"}}>
                            <Box
                                padding="3"
                                display="flex"
                                flexDirection="column"
                                justifyContent="space-between"
                                height="100%"
                            >
                                <Box
                                    display="grid"
                                    gridTemplateColumns="repeat(2, 1fr)"
                                    gap="8"
                                >
                                    <Box>
                                        {areScoutRegionsFetched &&
                                            <InputSelect
                                                ref={unitRef}
                                                initValue={undefined}
                                                label={'Chorągiew'}
                                                placeholderText={'Wybierz chorągiew z listy'}
                                                options={scoutRegions ?? []}
                                                isRequired={true}
                                                getSelectedOption={setSelectedScoutRegion}
                                            />}
                                        <InputSelect
                                            ref={ScoutRankRef}
                                            initValue={selectedScoutRankOption}
                                            label={'Stopień harcerski'}
                                            placeholderText={'Wybierz stopień:'}
                                            options={ScoutRankOptions}
                                            isRequired={true}
                                            getSelectedOption={setSelectedScoutRankOption}
                                        />
                                        <InputSelect 
                                            ref={InstructorRankRef}
                                            initValue={selectedInstructorRangeOption}
                                            label={'Stopień instruktorski'}
                                            placeholderText={'Wybierz stopień:'}
                                            options={instructorRangeOptions}
                                            isRequired={true}
                                            getSelectedOption={setSelectedInstructorRangeOption}
                                        />
                                        {(['PWD', 'PHM', 'HM'].includes(selectedInstructorRangeOption?.label!)) && (
                                            <InputDate
                                                ref={pwdDateRef}
                                                initValue={scout?.PwdDate ?? ''}
                                                label={'Data zdobycia pwd'} 
                                                isRequired={true}
                                            />
                                        )}
                                        {(['PHM', 'HM'].includes(selectedInstructorRangeOption?.label!)) && (
                                            <InputDate
                                                ref={phmDateRef}
                                                initValue={scout?.PhmDate ?? ''}
                                                label={'Data zdobycia phm'} 
                                                isRequired={true}
                                            />
                                        )}
                                        {(['HM'].includes(selectedInstructorRangeOption?.label!)) && (
                                            <InputDate
                                                ref={hmDateRef}
                                                initValue={scout?.HmDate ?? ''}
                                                label={'Data zdobycia hm'} 
                                                isRequired={true}
                                            />
                                        )}
                                    </Box>
                                    <Box>
                                        <InputText
                                            ref={corporateIDRef}
                                            label={'Numer książeczki służbowej'} 
                                            maxLength={20}
                                            placeholderText={'123/P12/2000'} 
                                            initValue={scout?.CorporateId ?? ''}
                                            isRequired={false}
                                        />
                                        <InputText
                                            ref={scoutFleurDeLisIDRef}
                                            label={'Numer lilijki instruktorskiej'} 
                                            placeholderText={'1234'}
                                            maxLength={20}
                                            initValue={scout?.ScoutFleurDeLisId ?? ''}
                                            isRequired={false}
                                        />
                                        <InputNumberDate
                                            ref={scoutPledgeDateRef}
                                            label={'Data złożenia przyrzeczenia'}
                                        />
                                    </Box>
                                </Box>
                                <Box>
                                    <RequiredMark withDescription />
                                    {isEditMode ? (
                                        <>
                                            <ErrorMessage text={submittingError} />
                                            <HStack>
                                                <Button
                                                    width="full"
                                                    variant='outline'
                                                    padding="3"
                                                    mt="5"
                                                    colorScheme="brand"
                                                    type="button"
                                                    onClick={() => cancelEdit()}
                                                >
                                                    Anuluj
                                                </Button>
                                                <ButtonSubmit isSubmitting={submitting} text={'Aktualizuj dane harcerskie'} />
                                            </HStack>
                                        </>
                                    ) : (
                                        <>
                                            <ErrorMessage text={submittingError} />
                                            <ButtonSubmit isSubmitting={submitting} text={'Zapisz dane harcerskie'} />
                                        </>
                                    )}
                                </Box>
                            </Box>
                        </form>
                    </Box>
                </Box>
            )
        }
    }
    else
    {
        return <LoadingWidget  withImage={true} image="url('/images/cone.jpg')" />
    }
};

export default ScoutProfile;